import React, { useState } from 'react';
import { db } from '../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { MAZE_JOURNEYS, AWARENESS_TASKS, MCQ_QUIZZES, CHESS_MATH, DANGER_ZONE } from './ArcadeCurriculum';

export default function ArcadePanel({ activeClass }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('AWARENESS'); // AWARENESS, DANGER, MCQ, MATH, MAZE

    const launchGame = async (gameMode, dataPayload, successMsg) => {
        if (!activeClass) return toast.error("No active class!");
        await updateDoc(doc(db, "classes", activeClass.id), {
            classMode: 'ARCADE_MONITOR', 
            arcadeData: { mode: gameMode, ...dataPayload, startTime: Date.now() },
            studentFens: {}, studentArcadeStats: {}
        });
        toast.success(successMsg);
    };

    const categories = [
        { id: 'AWARENESS', name: '📍 Awareness', color: '#8b5cf6', data: AWARENESS_TASKS, type: 'AWARENESS' },
        { id: 'DANGER', name: '⚠️ Danger', color: '#ef4444', data: DANGER_ZONE, type: 'DANGER' },
        { id: 'MCQ', name: '🧠 Quizzes', color: '#3b82f6', data: MCQ_QUIZZES, type: 'MCQ' },
        { id: 'MATH', name: '🧮 Math', color: '#ca8a04', data: CHESS_MATH, type: 'MATH' },
        { id: 'MAZE', name: '🍎 Mazes', color: '#10b981', data: MAZE_JOURNEYS, type: 'MAZE' }
    ];

    // Shared style for buttons
    const btnStyle = {
        width: '100%',
        padding: '12px 16px', 
        background: 'var(--bg-primary)', 
        color: 'var(--text-primary)', 
        borderRadius: '12px', 
        cursor: 'pointer', 
        textAlign: 'left', 
        fontSize: '13px', 
        fontWeight: 'bold',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        lineHeight: '1.4',
        border: '1.5px solid white',
        boxShadow: 'var(--neu-shadow)',
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px'
    };

    const handleMouseOver = (e) => {
        e.currentTarget.style.boxShadow = 'var(--neu-shadow-hover)';
        e.currentTarget.style.transform = 'translateY(-1px)';
    };

    const handleMouseOut = (e) => {
        e.currentTarget.style.boxShadow = 'var(--neu-shadow)';
        e.currentTarget.style.transform = 'translateY(0)';
    };

    // Filter items based on search query
    const getAllFilteredItems = () => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return null;
        
        const results = [];
        // Search in awareness
        AWARENESS_TASKS.forEach(t => {
            if (t.title.toLowerCase().includes(query) || (t.question && t.question.toLowerCase().includes(query))) {
                results.push({ type: 'AWARENESS', data: t, title: `📍 Awareness: ${t.title}` });
            }
        });
        // Search in danger
        DANGER_ZONE.forEach(t => {
            if (t.title.toLowerCase().includes(query) || (t.question && t.question.toLowerCase().includes(query))) {
                results.push({ type: 'DANGER', data: t, title: `⚠️ Danger: ${t.title}` });
            }
        });
        // Search in MCQ
        MCQ_QUIZZES.forEach(t => {
            if (t.question.toLowerCase().includes(query)) {
                results.push({ type: 'MCQ', data: t, title: `🧠 Quiz: ${t.question}` });
            }
        });
        // Search in Math
        CHESS_MATH.forEach(t => {
            if (t.title.toLowerCase().includes(query) || (t.question && t.question.toLowerCase().includes(query))) {
                results.push({ type: 'MATH', data: t, title: `🧮 Math: ${t.title}` });
            }
        });
        // Search in Mazes
        MAZE_JOURNEYS.forEach(t => {
            if (t.title.toLowerCase().includes(query)) {
                results.push({ type: 'MAZE', data: { category: 'MAZE', title: t.title, levels: t.levels }, title: `🍎 Maze: ${t.title}` });
            }
        });
        return results;
    };

    const searchResults = getAllFilteredItems();

    return (
        <div style={{ padding: '15px', display: 'flex', flexDirection: 'column', gap: '15px', height: '100%', boxSizing: 'border-box' }}>
            
            {/* Search Input */}
            <div style={{ position: 'relative', width: '100%', flexShrink: 0 }}>
                <input 
                    type="text" 
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="🔍 Search arcade chapters & tasks..."
                    style={{
                        width: '100%',
                        padding: '12px 15px',
                        paddingRight: searchQuery ? '35px' : '15px',
                        borderRadius: '12px',
                        background: 'var(--bg-primary)',
                        border: '1.5px solid white',
                        color: 'var(--text-primary)',
                        boxShadow: 'var(--neu-shadow-inset)',
                        outline: 'none',
                        fontSize: '13px',
                        fontWeight: '600'
                    }}
                />
                {searchQuery && (
                    <button 
                        onClick={() => setSearchQuery('')}
                        style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'transparent',
                            border: 'none',
                            color: '#ef4444',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}
                    >
                        ✖
                    </button>
                )}
            </div>

            {searchQuery ? (
                /* Search Results View */
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>
                        Search Results ({searchResults.length})
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', paddingRight: '5px' }}>
                        {searchResults.length === 0 ? (
                            <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center', marginTop: '20px', fontSize: '13px' }}>
                                No matching tasks found.
                            </div>
                        ) : (
                            searchResults.map((item, idx) => (
                                <button 
                                    key={idx}
                                    onClick={() => launchGame(item.type, item.data, `Launched: ${item.title}`)}
                                    style={btnStyle}
                                    onMouseOver={handleMouseOver}
                                    onMouseOut={handleMouseOut}
                                >
                                    <span>{item.title}</span>
                                    <span style={{ color: '#8b5cf6', fontSize: '14px' }}>▶</span>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            ) : (
                /* Category Tabs View */
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, gap: '12px' }}>
                    {/* Category Selector Tabs */}
                    <div style={{ 
                        display: 'flex', 
                        gap: '6px', 
                        overflowX: 'auto', 
                        paddingBottom: '5px', 
                        flexShrink: 0,
                        borderBottom: '1px solid rgba(163, 177, 198, 0.4)'
                    }}>
                        {categories.map(cat => {
                            const isSel = activeTab === cat.id;
                            return (
                                <button
                                    key={cat.id}
                                    onClick={() => setActiveTab(cat.id)}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        fontWeight: '800',
                                        whiteSpace: 'nowrap',
                                        background: isSel ? cat.color : 'transparent',
                                        color: isSel ? 'white' : 'var(--text-secondary)',
                                        boxShadow: isSel ? '0 4px 10px rgba(0,0,0,0.15)' : 'none',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {cat.name}
                                </button>
                            );
                        })}
                    </div>

                    {/* Active Category Content List */}
                    <div style={{ flex: 1, overflowY: 'auto', paddingRight: '5px' }}>
                        {categories.map(cat => {
                            if (activeTab !== cat.id) return null;
                            return (
                                <div key={cat.id} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {cat.name} Chapter Tasks
                                    </div>
                                    {cat.data.map(task => {
                                        let launchPayload = task;
                                        let launchTitle = task.title || task.question;
                                        if (cat.id === 'MAZE') {
                                            launchPayload = { category: 'MAZE', title: task.title, levels: task.levels };
                                        }
                                        
                                        // Specific text styling overrides for different categories to retain meaning
                                        let textStyle = {};
                                        if (cat.id === 'DANGER') textStyle = { color: '#b91c1c' };
                                        else if (cat.id === 'MATH') textStyle = { color: '#a16207' };
                                        
                                        return (
                                            <button 
                                                key={task.id}
                                                onClick={() => launchGame(cat.type, launchPayload, `Launched: ${launchTitle}`)}
                                                style={{...btnStyle, ...textStyle}}
                                                onMouseOver={handleMouseOver}
                                                onMouseOut={handleMouseOut}
                                            >
                                                <span style={{ textAlign: 'left' }}>
                                                    {cat.id === 'MCQ' ? `Quiz: ${task.question}` : task.title}
                                                </span>
                                                <span style={{ color: cat.color, fontSize: '15px' }}>▶</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
